/**
 * Sync-state badge for AccessGroup (and similar controller-backed
 * resources). Color palette matches the rest of the portal's Tailwind-
 * based status pills.
 *
 *   pending   amber
 *   in_flight blue
 *   synced    green
 *   failed    red
 *   drifted   orange
 */
import type { ReactElement } from 'react';

export type SyncStatus =
  | 'synced'
  | 'pending'
  | 'in_flight'
  | 'failed'
  | 'drifted';

const STYLES: Record<SyncStatus, string> = {
  synced: 'bg-secondary-container text-on-secondary-container border-secondary-fixed-dim',
  pending: 'bg-tertiary-fixed text-on-tertiary-fixed-variant border-tertiary-fixed-dim',
  in_flight: 'bg-secondary-container text-on-secondary-container border-secondary-fixed-dim',
  failed: 'bg-error-container text-on-error-container border-error-container',
  drifted: 'bg-tertiary-fixed text-on-tertiary-fixed-variant border-tertiary-fixed-dim',
};

const LABELS: Record<SyncStatus, string> = {
  synced: 'Synced',
  pending: 'Pending',
  in_flight: 'In flight',
  failed: 'Failed',
  drifted: 'Drifted',
};

// Lookup helpers — the ESLint security/detect-object-injection rule flags
// dynamic key access on plain objects; using Map removes the sink.
const STYLE_MAP = new Map<SyncStatus, string>(Object.entries(STYLES) as [SyncStatus, string][]);
const LABEL_MAP = new Map<SyncStatus, string>(Object.entries(LABELS) as [SyncStatus, string][]);

export function SyncStatusBadge({ status }: { status: SyncStatus }): ReactElement {
  // Fallback to neutral if the API returns something we don't know
  // about (e.g. a future enum value during a rolling upgrade).
  const cls = STYLE_MAP.get(status) ?? 'bg-surface-container text-on-surface border-outline-variant';
  const label = LABEL_MAP.get(status) ?? status;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}
